export type ApiErrorCode =
  | 'INVALID_KEY'
  | 'CONFLICT'
  | 'NOT_FOUND'
  | 'NOT_CONNECTED'
  | 'CONFIGURATION'
  | 'BUDS_UNREACHABLE'
  | 'APP_BRIDGE_TIMEOUT'
  | 'TIMEOUT'
  | 'UNKNOWN';

export class ShopifyAppApiError extends Error {
  override name = 'ShopifyAppApiError';
  constructor(
    public readonly code: ApiErrorCode,
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

const REQUEST_TIMEOUT_MS = 15_000;
const APP_BRIDGE_TIMEOUT_MS = 5_000;

async function freshIdToken(): Promise<string> {
  const deadline = Date.now() + APP_BRIDGE_TIMEOUT_MS;
  while (Date.now() < deadline) {
    const bridge = typeof window !== 'undefined' ? window.shopify : undefined;
    if (bridge && typeof bridge.idToken === 'function') {
      return bridge.idToken();
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  throw new ShopifyAppApiError(
    'APP_BRIDGE_TIMEOUT',
    0,
    'App Bridge failed to load. Reload the page from your Shopify admin.',
  );
}

function publicBase(): string {
  const base = import.meta.env.VITE_API_PUBLIC_URL;
  if (!base) throw new Error('VITE_API_PUBLIC_URL is not set');
  return base.replace(/\/$/, '');
}

function decodeCode(status: number, code?: string): ApiErrorCode {
  if (
    code === 'INVALID_KEY' ||
    code === 'CONFLICT' ||
    code === 'NOT_FOUND' ||
    code === 'NOT_CONNECTED' ||
    code === 'CONFIGURATION' ||
    code === 'BUDS_UNREACHABLE'
  )
    return code;
  if (status === 401) return 'INVALID_KEY';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  return 'UNKNOWN';
}

export interface CallOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Additional headers (e.g. `x-api-key` on /link). */
  extraHeaders?: Record<string, string>;
}

export async function callApi<T = unknown>(
  path: string,
  options: CallOptions = {},
): Promise<T> {
  const idToken = await freshIdToken();
  const headers: Record<string, string> = {
    authorization: `Bearer ${idToken}`,
    ...(options.extraHeaders ?? {}),
  };
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(`${publicBase()}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body:
        options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: controller.signal,
    });
  } catch (err) {
    if ((err as DOMException).name === 'AbortError') {
      throw new ShopifyAppApiError(
        'TIMEOUT',
        0,
        'Buds did not respond in time. Try again in a moment.',
      );
    }
    throw new ShopifyAppApiError(
      'BUDS_UNREACHABLE',
      0,
      'Could not reach Buds. Try again in a moment.',
    );
  } finally {
    clearTimeout(timeout);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    const payload = json as { message?: string; code?: string } | null;
    throw new ShopifyAppApiError(
      decodeCode(res.status, payload?.code),
      res.status,
      payload?.message ?? `HTTP ${res.status}`,
    );
  }
  return json as T;
}
